import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fileUrl } from "@/lib/api";
import { ShoppingCart, Check, ImageOff, Zap } from "lucide-react";
import { toast } from "sonner";
import { OrganicBadge } from "@/components/OrganicBadge";

const categoryColors = {
  pesticides: "bg-rose-50 text-rose-700 border-rose-200",
  herbicides: "bg-amber-50 text-amber-700 border-amber-200",
  fertilizers: "bg-emerald-50 text-emerald-700 border-emerald-200",
  seeds: "bg-lime-50 text-lime-700 border-lime-200",
  tools: "bg-sky-50 text-sky-700 border-sky-200",
  organic_pesticides: "bg-green-50 text-green-800 border-green-300",
  organic_produce: "bg-green-50 text-green-800 border-green-300",
  residue_free: "bg-teal-50 text-teal-800 border-teal-300",
};

const ProductCard = ({ product }) => {
  const { t, lang, addToCart, cart } = useApp();
  const navigate = useNavigate();
  const inCart = cart.some((it) => it.product_id === product.id);
  const outOfStock = product.stock <= 0;

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    toast.success(lang === "hi" ? "कार्ट में जोड़ा गया" : "Added to cart");
  };

  const handleBuyNow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!inCart) addToCart(product);
    navigate("/checkout");
  };

  const name = lang === "hi" ? product.name_hi : product.name_en;
  const img = fileUrl(product.image_path);

  return (
    <Link
      to={`/product/${product.id}`}
      className="group block bg-white border border-[#E2E8F0] rounded-xl overflow-hidden hover-lift"
      data-testid={`product-card-${product.id}`}
    >
      <div className="aspect-square bg-[#EFEBE2] relative overflow-hidden">
        {img ? (
          <img
            src={img}
            alt={name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 leaf-pattern">
            <ImageOff className="w-10 h-10 mb-1 opacity-50" />
            <span className="text-xs">No Image</span>
          </div>
        )}
        <div className="absolute top-2 left-2">
          <Badge
            variant="outline"
            className={`text-[10px] uppercase tracking-wider font-semibold ${categoryColors[product.category] || ""}`}
          >
            {t(product.category)}
          </Badge>
        </div>
        <div className="absolute bottom-2 left-2">
          <OrganicBadge category={product.category} />
        </div>
        {outOfStock && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white font-semibold text-sm">{t("outOfStock")}</span>
          </div>
        )}
      </div>
      <div className="p-3 sm:p-4">
        <h3
          className={`font-heading font-semibold text-[#1E293B] text-sm sm:text-base line-clamp-2 min-h-[2.5rem] ${lang === "hi" ? "font-devanagari" : ""}`}
          data-testid={`product-name-${product.id}`}
        >
          {name}
        </h3>
        <p className="text-xs text-slate-500 mt-1">{product.unit}</p>
        <div className="mt-3 flex items-end justify-between gap-2">
          <div>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">{t("price")}</p>
            <p className="font-heading font-bold text-lg text-[#2D5A27]" data-testid={`product-price-${product.id}`}>
              ₹{product.price.toFixed(0)}
            </p>
          </div>
          <Button
            onClick={handleAdd}
            disabled={outOfStock}
            data-testid={`add-to-cart-${product.id}`}
            size="sm"
            className={`rounded-full h-9 px-3 text-xs font-semibold pressable ${
              inCart
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                : "bg-[#2D5A27] hover:bg-[#21421D] text-white"
            }`}
          >
            {inCart ? <Check className="w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}
          </Button>
        </div>

        {/* Buy Now (Flipkart-style) */}
        <Button
          onClick={handleBuyNow}
          disabled={outOfStock}
          data-testid={`buy-now-${product.id}`}
          size="sm"
          className="mt-2 w-full rounded-full h-9 bg-[#D97706] hover:bg-[#B45309] text-white text-xs font-semibold pressable"
        >
          <Zap className="w-3.5 h-3.5 mr-1 fill-white" /> {t("buyNow")}
        </Button>
      </div>
    </Link>
  );
};

export default ProductCard;
